import { poe1, poe2 } from '../../game';
import {
  Filter,
  filterSection,
  TierCosmetic,
  GearHighlightConfig,
  JewelleryHighlightConfig,
  WeaponFilter,
  ArmourFilter,
  JewelleryFilter,
} from '../models/filter-models';

export interface RawFilterDiff {
  key: string;
  currentValue: unknown;
  newValue: unknown;
  groupLabel?: string;
  label?: string;
}

const TIER_COSMETIC_ARRAY_KEYS = [
  'uniqueTierCosmetics',
  'chanceBaseTierCosmetics',
  'divCardTierCosmetics',
] as const;

const MY_GEAR_SCALAR_FIELDS = ['baseTypeTier', 'rarity', 'itemLevel', 'unidentifiedTier'] as const;
const MY_JEWELLERY_SCALAR_FIELDS = ['rarity', 'itemLevel', 'unidentifiedTier'] as const;
const PRESET_MANAGED_JEWELLERY_SCALAR_FIELDS = ['rarity', 'itemLevel', 'unidentifiedTier', 'show'] as const;

const PRESET_MANAGED_GEAR_HIGHLIGHT_KEYS = [
  'regularNormal', 'regularMagic', 'regularRare',
  'lowTierMagic', 'lowTierRare',
  'rareMinTier', 'magicMinTier', 'lowTierRareMinTier', 'lowTierMagicMinTier',
] as const;

const PRESET_MANAGED_JEWELLERY_HIGHLIGHT_KEYS = [
  'regularNormal', 'regularMagic', 'magicMinTier', 'rareMinTier',
] as const;

const PRESET_TIERING_FIELDS: { key: keyof Filter; label: string; groupLabel?: string; poe1Only?: boolean }[] = [
  { key: 'tieringMode', label: 'Currency Tiering Mode', groupLabel: 'Tiering' },
  { key: 'uniquesTieringMode', label: 'Uniques Tiering Mode', groupLabel: 'Tiering' },
  { key: 'equipmentTieringMode', label: 'Equipment Tiering Mode', groupLabel: 'Tiering' },
  { key: 'clusterJewelTieringMode', label: 'Cluster Jewel Tiering Mode', groupLabel: 'Tiering', poe1Only: true },
  { key: 'showClassSpecificUniquesForClasses', label: 'Class Specific Uniques' },
];

const TOKEN_REPLACEMENTS: Record<string, string> = {
  ilevel: 'iLvl',
  ilvl: 'iLvl',
  min: 'Min.',
  max: 'Max.',
  qlty: 'Quality',
  lvl: 'Lvl',
  i: 'i',
};

let ruleKeyToSection: Map<string, filterSection> | null = null;
let ruleToggleKeys: string[] | null = null;

function buildRuleMaps(): void {
  if (ruleKeyToSection) return;
  ruleKeyToSection = new Map();
  const keys: string[] = [];
  for (const group of Filter.sections) {
    for (const rule of group.rules) {
      if (poe1 && rule.poe2) continue;
      if (poe2 && rule.poe1) continue;
      const key = rule.key as string;
      ruleKeyToSection.set(key, group.section);
      keys.push(key);
    }
  }
  ruleToggleKeys = keys.sort((a, b) => b.length - a.length);
}

function isComparableScalarValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const t = typeof value;
  if (t === 'boolean' || t === 'number' || t === 'string') return true;
  if (Array.isArray(value)) {
    return value.every(v => v === null || ['boolean', 'number', 'string'].includes(typeof v));
  }
  return false;
}

function longestRulePrefix(filterKey: string): string | null {
  buildRuleMaps();
  for (const ruleKey of ruleToggleKeys!) {
    if (filterKey === ruleKey || filterKey.startsWith(ruleKey)) return ruleKey;
  }
  return null;
}

function splitCamelCase(text: string): string[] {
  return text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .split(/[\s._]+/)
    .filter(Boolean);
}

function formatToken(token: string): string {
  const lower = token.toLowerCase();
  if (TOKEN_REPLACEMENTS[lower] !== undefined) {
    return TOKEN_REPLACEMENTS[lower];
  }
  if (/^\d+$/.test(token)) return token;
  return token.charAt(0).toUpperCase() + token.slice(1);
}

function formatSegment(segment: string): string {
  const tokens = splitCamelCase(segment);
  if (tokens.length === 0) return segment;
  const formatted = tokens.map(formatToken);
  formatted[0] = formatted[0].charAt(0).toUpperCase() + formatted[0].slice(1);
  return formatted.join(' ');
}

export function formatPropertyLabel(key: string): string {
  const parts = key.split('.');
  return parts.map(formatSegment).join(' · ');
}

export function formatEnumString(value: string): string {
  return splitCamelCase(value).map(formatToken).join(' ');
}

export type ArrayDiffItemStatus = 'unchanged' | 'removed' | 'added';

export interface ArrayDiffItem {
  text: string;
  status: ArrayDiffItemStatus;
}

export function isPrimitiveArray(value: unknown): value is (string | number | boolean | null)[] {
  return Array.isArray(value) && value.every(v => v === null || ['boolean', 'number', 'string'].includes(typeof v));
}

function formatScalarValue(value: unknown): string {
  if (value === null || value === undefined) return '✕';
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (/^[A-Z]/.test(value) && /[a-z]/.test(value) && !value.includes(' ')) {
      return formatEnumString(value);
    }
    return value;
  }
  return String(value);
}

export function buildArrayDiff(current: unknown[], next: unknown[]): ArrayDiffItem[] {
  const items: ArrayDiffItem[] = [];
  for (const item of next) {
    items.push({
      text: formatScalarValue(item),
      status: current.some(c => c === item) ? 'unchanged' : 'added',
    });
  }
  for (const item of current) {
    if (!next.some(n => n === item)) {
      items.push({ text: formatScalarValue(item), status: 'removed' });
    }
  }
  return items;
}

export function formatChangeValue(value: unknown): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return JSON.stringify(value);
  }
  return formatScalarValue(value);
}

export function getSectionForKey(key: string): filterSection {
  const ruleKey = longestRulePrefix(key.split('.')[0]);
  if (ruleKey) return ruleKeyToSection!.get(ruleKey)!;

  const root = key.split('.')[0];
  if (root === 'uniqueTierCosmetics' || root === 'chanceBaseTierCosmetics') return 'uniques';
  if (root === 'showClassSpecificUniquesForClasses') return 'uniques';
  if (root === 'divCardTierCosmetics') return 'currency';
  if (root === 'tieringMode') return 'currency';
  if (root === 'myGear' || root === 'myJewellery') return 'myEquipment';
  return 'other';
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

function pushDiff(diffs: RawFilterDiff[], diff: RawFilterDiff): void {
  diffs.push(diff);
}

function collectPresetTieringDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  for (const field of PRESET_TIERING_FIELDS) {
    if (field.poe1Only && !poe1) continue;
    const current = a[field.key];
    const next = b[field.key];
    if (!isComparableScalarValue(current) || !isComparableScalarValue(next)) continue;
    if (!valuesEqual(current, next)) {
      pushDiff(diffs, {
        key: field.key,
        currentValue: current,
        newValue: next,
        label: field.label,
        groupLabel: field.groupLabel,
      });
    }
  }
}

function collectSectionScalarDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  const filter = new Filter();
  for (const key of Object.keys(filter)) {
    if (!longestRulePrefix(key)) continue;
    const current = (a as any)[key];
    const next = (b as any)[key];
    if (!isComparableScalarValue(current) || !isComparableScalarValue(next)) continue;
    if (!valuesEqual(current, next)) {
      pushDiff(diffs, { key, currentValue: current, newValue: next });
    }
  }
}

function getTierCosmeticId(entry: TierCosmetic): string {
  return String(entry.uniqueTier ?? entry.chanceBaseTier ?? entry.tier ?? entry.name ?? entry.order);
}

function findTierCosmeticEntry(arr: TierCosmetic[], id: string): TierCosmetic | undefined {
  return arr.find(e => getTierCosmeticId(e) === id);
}

function collectTierCosmeticDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  for (const arrayKey of TIER_COSMETIC_ARRAY_KEYS) {
    const aArr = (a as any)[arrayKey] as TierCosmetic[];
    const bArr = (b as any)[arrayKey] as TierCosmetic[];
    const ids = new Set<string>();
    for (const entry of aArr) ids.add(getTierCosmeticId(entry));
    for (const entry of bArr) ids.add(getTierCosmeticId(entry));

    for (const id of ids) {
      const aEntry = findTierCosmeticEntry(aArr, id);
      const bEntry = findTierCosmeticEntry(bArr, id);
      const current = aEntry?.displayOptions?.active;
      const next = bEntry?.displayOptions?.active;
      if (!valuesEqual(current, next)) {
        pushDiff(diffs, {
          key: `${arrayKey}.${id}.active`,
          currentValue: current,
          newValue: next,
        });
      }
    }
  }
}

function isPresetEquipmentFilter(f: { fromClassPreset?: boolean; fromProgressPreset?: boolean }): boolean {
  return !!(f.fromClassPreset || f.fromProgressPreset);
}

function pickMyGearFilter(filter: Filter): WeaponFilter | ArmourFilter | undefined {
  return filter.armourFilters.find(isPresetEquipmentFilter)
    ?? filter.weaponFilters.find(isPresetEquipmentFilter);
}

function listPresetGearFilters(filter: Filter): (WeaponFilter | ArmourFilter)[] {
  return [...filter.weaponFilters, ...filter.armourFilters].filter(isPresetEquipmentFilter);
}

function collectPresetWeaponTypes(filter: Filter): string[] {
  const types = new Set<string>();
  for (const weaponFilter of filter.weaponFilters) {
    if (!isPresetEquipmentFilter(weaponFilter)) continue;
    for (const weaponType of weaponFilter.weaponType) types.add(weaponType);
  }
  return [...types].sort();
}

function collectPresetDefenceTypes(filter: Filter): string[] {
  const types = new Set<string>();
  for (const armourFilter of filter.armourFilters) {
    if (!isPresetEquipmentFilter(armourFilter)) continue;
    for (const defenceType of armourFilter.defenceType) types.add(defenceType);
  }
  return [...types].sort();
}

function pushGearArrayDiffIfChanged(
  diffs: RawFilterDiff[],
  key: string,
  label: string,
  groupLabel: string,
  current: string[],
  next: string[],
): void {
  if (!valuesEqual(current, next)) {
    pushDiff(diffs, {
      key,
      currentValue: current,
      newValue: next,
      groupLabel,
      label,
    });
  }
}

function listPresetJewelleryFilters(filter: Filter): JewelleryFilter[] {
  return filter.jewelleryFilters.filter(isPresetEquipmentFilter);
}

function firstFieldDiffAcrossFilters<T extends object>(
  aFilters: T[],
  bFilters: T[],
  read: (filter: T | undefined) => unknown,
): { current: unknown; next: unknown } | null {
  const count = Math.max(aFilters.length, bFilters.length);
  for (let i = 0; i < count; i++) {
    const current = read(aFilters[i]);
    const next = read(bFilters[i]);
    if (!valuesEqual(current, next)) {
      return { current, next };
    }
  }
  return null;
}

function getGearHighlightConfigKeys(): string[] {
  const config = new GearHighlightConfig();
  return Object.keys(config).filter(key => {
    if (key === 'equipmentType') return false;
    if (!poe1 && GearHighlightConfig.poe1OnlyProperties.includes(key as keyof GearHighlightConfig)) return false;
    if (poe1 && GearHighlightConfig.poe2OnlyProperties.includes(key as keyof GearHighlightConfig)) return false;
    const value = (config as any)[key];
    return typeof value === 'boolean' || typeof value === 'number' || value === null || value === undefined;
  });
}

function getJewelleryHighlightConfigKeys(): string[] {
  const config = new JewelleryHighlightConfig();
  return Object.keys(config).filter(key => {
    if (key === 'equipmentType') return false;
    if (!poe1 && JewelleryHighlightConfig.poe1OnlyProperties.includes(key as keyof JewelleryHighlightConfig)) return false;
    if (poe1 && JewelleryHighlightConfig.poe2OnlyProperties.includes(key as keyof JewelleryHighlightConfig)) return false;
    const value = (config as any)[key];
    return typeof value === 'boolean' || typeof value === 'number' || value === null || value === undefined;
  });
}

function collectMyGearDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  const hasAny = [...a.weaponFilters, ...a.armourFilters].some(isPresetEquipmentFilter)
    || [...b.weaponFilters, ...b.armourFilters].some(isPresetEquipmentFilter);
  if (!hasAny) return;

  const currentFilter = pickMyGearFilter(a);
  const nextFilter = pickMyGearFilter(b);
  if (!currentFilter && !nextFilter) return;

  const groupLabel = 'Favourite Weapons & Armour Changes';

  for (const field of MY_GEAR_SCALAR_FIELDS) {
    const current = currentFilter ? (currentFilter as any)[field] : undefined;
    const next = nextFilter ? (nextFilter as any)[field] : undefined;
    if (!valuesEqual(current, next)) {
      pushDiff(diffs, {
        key: `myGear.${field}`,
        currentValue: current,
        newValue: next,
        groupLabel,
        label: formatSegment(field),
      });
    }
  }

  for (const field of getGearHighlightConfigKeys()) {
    const current = currentFilter?.highlightConfig ? (currentFilter.highlightConfig as any)[field] : undefined;
    const next = nextFilter?.highlightConfig ? (nextFilter.highlightConfig as any)[field] : undefined;
    if (!valuesEqual(current, next)) {
      pushDiff(diffs, {
        key: `myGear.highlightConfig.${field}`,
        currentValue: current,
        newValue: next,
        groupLabel,
        label: formatSegment(field),
      });
    }
  }

  pushGearArrayDiffIfChanged(
    diffs,
    'myGear.weaponType',
    'Weapon Classes',
    groupLabel,
    collectPresetWeaponTypes(a),
    collectPresetWeaponTypes(b),
  );
  pushGearArrayDiffIfChanged(
    diffs,
    'myGear.defenceType',
    'Defence Types',
    groupLabel,
    collectPresetDefenceTypes(a),
    collectPresetDefenceTypes(b),
  );
}

function collectPresetManagedMyGearDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  const aFilters = listPresetGearFilters(a);
  const bFilters = listPresetGearFilters(b);
  if (aFilters.length === 0 && bFilters.length === 0) return;

  const groupLabel = 'Favourite Weapons & Armour Changes';

  for (const field of MY_GEAR_SCALAR_FIELDS) {
    const diff = firstFieldDiffAcrossFilters(aFilters, bFilters, f => f ? (f as any)[field] : undefined);
    if (!diff) continue;
    pushDiff(diffs, {
      key: `myGear.${field}`,
      currentValue: diff.current,
      newValue: diff.next,
      groupLabel,
      label: formatSegment(field),
    });
  }

  for (const field of PRESET_MANAGED_GEAR_HIGHLIGHT_KEYS) {
    const diff = firstFieldDiffAcrossFilters(
      aFilters,
      bFilters,
      f => f?.highlightConfig ? (f.highlightConfig as any)[field] : undefined,
    );
    if (!diff) continue;
    pushDiff(diffs, {
      key: `myGear.highlightConfig.${field}`,
      currentValue: diff.current,
      newValue: diff.next,
      groupLabel,
      label: formatSegment(field),
    });
  }

  pushGearArrayDiffIfChanged(
    diffs,
    'myGear.weaponType',
    'Weapon Classes',
    groupLabel,
    collectPresetWeaponTypes(a),
    collectPresetWeaponTypes(b),
  );
  pushGearArrayDiffIfChanged(
    diffs,
    'myGear.defenceType',
    'Defence Types',
    groupLabel,
    collectPresetDefenceTypes(a),
    collectPresetDefenceTypes(b),
  );
}

function collectPresetManagedMyJewelleryDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  const aFilters = listPresetJewelleryFilters(a);
  const bFilters = listPresetJewelleryFilters(b);
  if (aFilters.length === 0 && bFilters.length === 0) return;

  const groupLabel = 'Favourite Jewellery Changes';

  for (const field of PRESET_MANAGED_JEWELLERY_SCALAR_FIELDS) {
    const diff = firstFieldDiffAcrossFilters(aFilters, bFilters, f => f ? (f as any)[field] : undefined);
    if (!diff) continue;
    pushDiff(diffs, {
      key: `myJewellery.${field}`,
      currentValue: diff.current,
      newValue: diff.next,
      groupLabel,
      label: formatSegment(field),
    });
  }

  for (const field of PRESET_MANAGED_JEWELLERY_HIGHLIGHT_KEYS) {
    const diff = firstFieldDiffAcrossFilters(
      aFilters,
      bFilters,
      f => f?.highlightConfig ? (f.highlightConfig as any)[field] : undefined,
    );
    if (!diff) continue;
    pushDiff(diffs, {
      key: `myJewellery.highlightConfig.${field}`,
      currentValue: diff.current,
      newValue: diff.next,
      groupLabel,
      label: formatSegment(field),
    });
  }
}

function collectMyJewelleryDiffs(a: Filter, b: Filter, diffs: RawFilterDiff[]): void {
  const hasAny = a.jewelleryFilters.some(isPresetEquipmentFilter)
    || b.jewelleryFilters.some(isPresetEquipmentFilter);
  if (!hasAny) return;

  const currentFilter = a.jewelleryFilters.find(isPresetEquipmentFilter);
  const nextFilter = b.jewelleryFilters.find(isPresetEquipmentFilter);
  if (!currentFilter && !nextFilter) return;

  const groupLabel = 'Favourite Jewellery Changes';

  for (const field of MY_JEWELLERY_SCALAR_FIELDS) {
    const current = currentFilter ? (currentFilter as any)[field] : undefined;
    const next = nextFilter ? (nextFilter as any)[field] : undefined;
    if (!valuesEqual(current, next)) {
      pushDiff(diffs, {
        key: `myJewellery.${field}`,
        currentValue: current,
        newValue: next,
        groupLabel,
        label: formatSegment(field),
      });
    }
  }

  for (const field of getJewelleryHighlightConfigKeys()) {
    const current = currentFilter?.highlightConfig ? (currentFilter.highlightConfig as any)[field] : undefined;
    const next = nextFilter?.highlightConfig ? (nextFilter.highlightConfig as any)[field] : undefined;
    if (!valuesEqual(current, next)) {
      pushDiff(diffs, {
        key: `myJewellery.highlightConfig.${field}`,
        currentValue: current,
        newValue: next,
        groupLabel,
        label: formatSegment(field),
      });
    }
  }
}

export function collectFilterDiffs(a: Filter, b: Filter): RawFilterDiff[] {
  const diffs: RawFilterDiff[] = [];
  collectPresetTieringDiffs(a, b, diffs);
  collectSectionScalarDiffs(a, b, diffs);
  collectTierCosmeticDiffs(a, b, diffs);
  collectMyGearDiffs(a, b, diffs);
  collectMyJewelleryDiffs(a, b, diffs);
  return diffs;
}

export function collectUserModificationDiffKeys(a: Filter, b: Filter): Set<string> {
  const diffs: RawFilterDiff[] = [];
  collectPresetTieringDiffs(a, b, diffs);
  collectSectionScalarDiffs(a, b, diffs);
  collectTierCosmeticDiffs(a, b, diffs);
  collectPresetManagedMyGearDiffs(a, b, diffs);
  collectPresetManagedMyJewelleryDiffs(a, b, diffs);
  return new Set(diffs.map(d => d.key));
}
